import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getOvernightStockBars,
  getStockBars,
} from "../marketData/marketDataApi";
import { connectStockMarketDataStream } from "../marketData/marketDataStream";
import ChartDrawingInteractionLayer from "../chartWorkspace/ChartDrawingInteractionLayer";
import {
  barsForSessionVisibility,
  barsInsideTimelineDomain,
  buildExtendedSessionBands,
  buildNewYork24HourAxis,
  buildNewYork24HourDomain,
  buildScheduledExtendedSessionBands,
} from "./professionalChartSessions";
import "./ProfessionalChart.css";
import "./ProfessionalHistoryChart.css";
import "./ProfessionalExtendedHours.css";
import { traceChartNavigation } from "../chartWorkspace/chartNavigationTrace";

const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1H", "4H", "1D"];
const HISTORY_RANGES = ["1D", "2D", "5D", "10D", "1M", "3M", "6M", "YTD", "1Y", "2Y", "5Y", "MAX"];
const MAX_VISIBLE_BARS = 90;
const VIEWPORT_STEP = 45;
const DRAG_STEP_PX = 12;
const MAX_FUTURE_SPACE_BARS = 55;
const HISTORY_BUFFER_MULTIPLIER = 1;
const EMA_PERIOD = 20;
const CHART_WIDTH = 1000;
const PRICE_HEIGHT = 300;
const VOLUME_TOP = 318;
const VOLUME_HEIGHT = 64;
const CHART_HEIGHT = 420;
const CHART_PLAN_EVENT = "nina:chart-trade-plan";
const ACTIVE_CANDLE_1M_LIMIT = 20;
const ACTIVE_CANDLE_BUCKET_MS = 5 * 60 * 1000;
const ACTIVE_STREAM_FRESHNESS_MS = 30 * 1000;
const QUOTE_EVIDENCE_FRESHNESS_MS = 60 * 1000;
const DELAYED_SIP_QUOTE_FRESHNESS_MS = 17 * 60 * 1000;
const ACTIVE_CANDLE_DERIVED_FLAG = "active_5m_from_authenticated_iex_1m";
const OVERNIGHT_ACTIVE_CANDLE_DERIVED_FLAG =
  "active_5m_from_authenticated_overnight_latest_1m";
const NEW_YORK_SESSION_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const TIMEFRAME_MINUTES = {
  "1m": 1,
  "5m": 5,
  "15m": 15,
  "30m": 30,
  "1H": 60,
  "4H": 240,
  "1D": 1440,
};

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeNavigationState(value = {}) {
  const viewportEnd = finiteNumber(value?.viewportEnd);
  const futureSpaceBars = finiteNumber(value?.futureSpaceBars);
  const normalizedViewportEnd =
    viewportEnd === null ? null : Math.max(0, Math.floor(viewportEnd));
  return {
    viewportEnd: normalizedViewportEnd,
    futureSpaceBars:
      normalizedViewportEnd === null || futureSpaceBars === null
        ? 0
        : Math.max(0, Math.floor(futureSpaceBars)),
  };
}

function formatPrice(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `$${number.toFixed(2)}`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatTime(value) {
  if (!value) return "Time unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Time unavailable";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatAxisTime(value, includeDate) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return includeDate
    ? parsed.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : parsed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function normalizeBars(payload) {
  const bars = Array.isArray(payload?.bars) ? payload.bars : [];
  return bars
    .map((bar) => {
      const open = finiteNumber(bar?.o);
      const high = finiteNumber(bar?.h);
      const low = finiteNumber(bar?.l);
      const close = finiteNumber(bar?.c);
      if ([open, high, low, close].some((value) => value === null)) return null;
      return {
        timestamp: bar?.t || null,
        open,
        high,
        low,
        close,
        volume: finiteNumber(bar?.v) || 0,
        vwap: finiteNumber(bar?.vw),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
}

function barsRequestKey(symbol, timeframe, feed, limit) {
  return [
    String(symbol || "").trim().toUpperCase(),
    String(timeframe || "").trim(),
    String(feed || "").trim().toLowerCase(),
    String(Number(limit)),
  ].join(":");
}

function matchingBundledBarsData(bundledBars, symbol, timeframe, feed, limit) {
  const request = bundledBars?.request;
  const data = bundledBars?.data;
  const expectedKey = barsRequestKey(symbol, timeframe, feed, limit);
  const requestKey = barsRequestKey(
    request?.symbol,
    request?.timeframe,
    request?.feed,
    request?.limit
  );
  const dataKey = barsRequestKey(
    data?.symbol,
    data?.timeframe,
    data?.source?.feed,
    limit
  );

  return request && data && requestKey === expectedKey && dataKey === expectedKey
    ? data
    : null;
}

function newYorkMarketSession(value) {
  if (!value) return "unknown";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsed);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const minutes = Number(values.hour) * 60 + Number(values.minute);
    if (!Number.isFinite(minutes)) return "unknown";
    if (values.weekday === "Sat" || values.weekday === "Sun") return "closed";
    if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) return "premarket";
    if (minutes >= 9 * 60 + 30 && minutes < 16 * 60) return "regular";
    if (minutes >= 16 * 60 && minutes < 20 * 60) return "after-hours";
    return "closed";
  } catch (_error) {
    return "unknown";
  }
}

function newYorkMarketDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    return NEW_YORK_SESSION_DATE_FORMATTER.format(parsed);
  } catch (_error) {
    return parsed.toISOString().slice(0, 10);
  }
}

function formatNewYorkTime(value) {
  if (!value) return "Time unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Time unavailable";
  return parsed.toLocaleTimeString([], {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function sessionQuoteBadge(
  snapshot,
  delayedSipSnapshot,
  streamedTrade,
  now = Date.now()
) {
  const nowTimestamp = Number(now);
  const nowIso = Number.isFinite(nowTimestamp)
    ? new Date(nowTimestamp).toISOString()
    : null;
  const session = newYorkMarketSession(nowIso);
  const labels = {
    premarket: "PREMARKET QUOTE · 15-MIN DELAY",
    regular: "LIVE QUOTE",
    "after-hours": "AFTER-HOURS QUOTE · 15-MIN DELAY",
    closed: "MARKET CLOSED",
    unknown: "MARKET DATA",
  };
  const label = labels[session] || labels.unknown;
  if (
    !Number.isFinite(nowTimestamp) ||
    !["premarket", "regular", "after-hours"].includes(session)
  ) {
    return { label, evidence: null };
  }

  const candidates = [];
  const addCandidate = (price, timestamp, freshnessMs) => {
    const normalizedPrice = finiteNumber(price);
    const normalizedTimestamp = Date.parse(timestamp);
    if (
      normalizedPrice === null ||
      normalizedPrice <= 0 ||
      !Number.isFinite(normalizedTimestamp)
    ) {
      return;
    }
    candidates.push({
      price: normalizedPrice,
      timestamp,
      timestampMs: normalizedTimestamp,
      freshnessMs,
    });
  };

  const authenticatedIexSnapshot =
    String(snapshot?.source?.provider || "").toLowerCase() === "alpaca" &&
    String(snapshot?.source?.feed || "").toLowerCase() === "iex";
  const authenticatedDelayedSipSnapshot =
    String(delayedSipSnapshot?.source?.provider || "").toLowerCase() === "alpaca" &&
    String(delayedSipSnapshot?.source?.feed || "").toLowerCase() === "delayed_sip";
  const evidenceSnapshot =
    session === "regular" && authenticatedIexSnapshot
      ? snapshot
      : session !== "regular" && authenticatedDelayedSipSnapshot
      ? delayedSipSnapshot
      : null;
  const freshnessMs =
    session === "regular"
      ? QUOTE_EVIDENCE_FRESHNESS_MS
      : DELAYED_SIP_QUOTE_FRESHNESS_MS;

  if (session === "regular") {
    addCandidate(
      streamedTrade?.p,
      streamedTrade?.t,
      QUOTE_EVIDENCE_FRESHNESS_MS
    );
  }
  if (evidenceSnapshot) {
    addCandidate(
      evidenceSnapshot?.latest_trade?.p,
      evidenceSnapshot?.latest_trade?.t,
      freshnessMs
    );
    addCandidate(
      evidenceSnapshot?.minute_bar?.c,
      evidenceSnapshot?.minute_bar?.t,
      freshnessMs
    );
    const bid = finiteNumber(evidenceSnapshot?.latest_quote?.bp);
    const ask = finiteNumber(evidenceSnapshot?.latest_quote?.ap);
    if (bid !== null && bid > 0 && ask !== null && ask > 0) {
      addCandidate(
        (bid + ask) / 2,
        evidenceSnapshot?.latest_quote?.t,
        freshnessMs
      );
    }
  }

  const currentDate = newYorkMarketDate(nowIso);
  const evidence = candidates
    .filter((candidate) => {
      const ageMs = nowTimestamp - candidate.timestampMs;
      return (
        newYorkMarketSession(candidate.timestamp) === session &&
        newYorkMarketDate(candidate.timestamp) === currentDate &&
        ageMs >= -5_000 &&
        ageMs <= candidate.freshnessMs
      );
    })
    .sort((left, right) => right.timestampMs - left.timestampMs)[0] || null;

  return { label, evidence };
}

function parsedTimestamp(value) {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : -Infinity;
}

function newestPayloadTimestamp(source, bars) {
  return Math.max(
    parsedTimestamp(source?.last_bar_at),
    parsedTimestamp(source?.last_update_at),
    ...(Array.isArray(bars) ? bars.map((bar) => parsedTimestamp(bar?.t)) : [])
  );
}

function laterTimestampValue(currentValue, nextValue) {
  return parsedTimestamp(nextValue) > parsedTimestamp(currentValue)
    ? nextValue
    : currentValue ?? nextValue;
}

function reconcileBackgroundSource(currentSource, nextSource, currentBars, nextBars) {
  const current = currentSource || {};
  const next = nextSource || {};
  const nextIsFresher =
    newestPayloadTimestamp(next, nextBars) >
    newestPayloadTimestamp(current, currentBars);
  const fresherProvenance = nextIsFresher ? next : current;
  const source = {
    ...current,
    ...next,
    last_bar_at: laterTimestampValue(current.last_bar_at, next.last_bar_at),
    last_update_at: laterTimestampValue(
      current.last_update_at,
      next.last_update_at
    ),
  };

  if (Object.prototype.hasOwnProperty.call(fresherProvenance, "refresh_mode")) {
    source.refresh_mode = fresherProvenance.refresh_mode;
  }
  if (Object.prototype.hasOwnProperty.call(fresherProvenance, "is_streaming")) {
    source.is_streaming = fresherProvenance.is_streaming;
  }
  return source;
}

function reconcileBackgroundBars(currentPayload, nextPayload) {
  const currentBars = Array.isArray(currentPayload?.bars) ? currentPayload.bars : [];
  const nextBars = Array.isArray(nextPayload?.bars) ? nextPayload.bars : [];
  const reconciledBars = [...currentBars];
  const indexesByTimestamp = new Map();
  let latestTimestamp = -Infinity;

  currentBars.forEach((bar, index) => {
    const timestamp = Date.parse(bar?.t);
    if (!Number.isFinite(timestamp)) return;
    indexesByTimestamp.set(bar.t, index);
    latestTimestamp = Math.max(latestTimestamp, timestamp);
  });

  const appendedByTimestamp = new Map();
  nextBars.forEach((bar) => {
    const timestamp = Date.parse(bar?.t);
    if (!Number.isFinite(timestamp)) return;

    const existingIndex = indexesByTimestamp.get(bar.t);
    if (existingIndex !== undefined) {
      reconciledBars[existingIndex] = bar;
      return;
    }

    if (timestamp > latestTimestamp) {
      appendedByTimestamp.set(bar.t, bar);
    }
  });

  const appendedBars = [...appendedByTimestamp.values()].sort(
    (left, right) => Date.parse(left.t) - Date.parse(right.t)
  );

  return {
    ...currentPayload,
    ...nextPayload,
    source: reconcileBackgroundSource(
      currentPayload?.source,
      nextPayload?.source,
      currentBars,
      nextBars
    ),
    bars: [...reconciledBars, ...appendedBars],
  };
}

function authenticatedActiveFiveMinuteBar(
  payload,
  requestedBucketTimestamp = null,
  {
    expectedFeed = "iex",
    forcePartial = false,
    streamCoverageStartedAtProviderTimestamp = null,
  } = {}
) {
  if (
    payload?.source?.provider !== "Alpaca" ||
    payload?.source?.feed !== expectedFeed ||
    !Array.isArray(payload?.bars)
  ) {
    return null;
  }

  const providerBars = payload.bars
    .map((bar) => {
      const timestamp = Date.parse(bar?.t);
      const open = finiteNumber(bar?.o);
      const high = finiteNumber(bar?.h);
      const low = finiteNumber(bar?.l);
      const close = finiteNumber(bar?.c);
      const volume = finiteNumber(bar?.v);
      if (
        !Number.isFinite(timestamp) ||
        [open, high, low, close, volume].some((value) => value === null)
      ) {
        return null;
      }
      return {
        timestamp,
        bucketTimestamp:
          Math.floor(timestamp / ACTIVE_CANDLE_BUCKET_MS) * ACTIVE_CANDLE_BUCKET_MS,
        open,
        high,
        low,
        close,
        volume,
        vwap: finiteNumber(bar?.vw),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.timestamp - right.timestamp);

  if (!providerBars.length) return null;

  const newestBucketTimestamp = providerBars[providerBars.length - 1].bucketTimestamp;
  const currentBucketTimestamp = Number.isFinite(requestedBucketTimestamp)
    ? Math.floor(requestedBucketTimestamp / ACTIVE_CANDLE_BUCKET_MS) *
      ACTIVE_CANDLE_BUCKET_MS
    : newestBucketTimestamp;
  const bucketBars = providerBars.filter(
    (bar) => bar.bucketTimestamp === currentBucketTimestamp
  );
  if (!bucketBars.length) return null;

  let vwapValue = 0;
  let vwapVolume = 0;
  bucketBars.forEach((bar) => {
    if (bar.vwap === null || bar.volume <= 0) return;
    vwapValue += bar.vwap * bar.volume;
    vwapVolume += bar.volume;
  });

  const hasKnownStreamCoverageStart = Number.isFinite(
    streamCoverageStartedAtProviderTimestamp
  );
  const derivedFlag =
    expectedFeed === "overnight"
      ? OVERNIGHT_ACTIVE_CANDLE_DERIVED_FLAG
      : ACTIVE_CANDLE_DERIVED_FLAG;
  return {
    t: new Date(currentBucketTimestamp).toISOString(),
    o: bucketBars[0].open,
    h: Math.max(...bucketBars.map((bar) => bar.high)),
    l: Math.min(...bucketBars.map((bar) => bar.low)),
    c: bucketBars[bucketBars.length - 1].close,
    v: bucketBars.reduce((total, bar) => total + bar.volume, 0),
    vw: vwapVolume > 0 ? vwapValue / vwapVolume : null,
    ...(forcePartial || hasKnownStreamCoverageStart
      ? {
          is_partial:
            forcePartial ||
            streamCoverageStartedAtProviderTimestamp > currentBucketTimestamp,
        }
      : {}),
    [derivedFlag]: true,
  };
}

function isDerivedActiveFiveMinuteBar(bar) {
  return (
    bar?.[ACTIVE_CANDLE_DERIVED_FLAG] === true ||
    bar?.[OVERNIGHT_ACTIVE_CANDLE_DERIVED_FLAG] === true
  );
}

function reconcileActiveFiveMinuteBar(
  currentPayload,
  activeBar,
  {
    allowPreviousDerivedReplacement = false,
    preserveMatchingNativeBar = false,
  } = {}
) {
  if (!currentPayload || !activeBar) return currentPayload;
  const currentBars = Array.isArray(currentPayload.bars) ? currentPayload.bars : [];
  const activeTimestamp = Date.parse(activeBar.t);
  let latestTimestamp = -Infinity;
  let matchingIndex = -1;

  currentBars.forEach((bar, index) => {
    const timestamp = Date.parse(bar?.t);
    if (!Number.isFinite(timestamp)) return;
    latestTimestamp = Math.max(latestTimestamp, timestamp);
    if (bar.t === activeBar.t) matchingIndex = index;
  });

  const replacesImmediatelyPreviousDerivedBar =
    allowPreviousDerivedReplacement &&
    matchingIndex >= 0 &&
    isDerivedActiveFiveMinuteBar(currentBars[matchingIndex]) &&
    latestTimestamp - activeTimestamp === ACTIVE_CANDLE_BUCKET_MS;
  const wouldReplaceNativeBar =
    preserveMatchingNativeBar &&
    matchingIndex >= 0 &&
    !isDerivedActiveFiveMinuteBar(currentBars[matchingIndex]);
  if (
    !Number.isFinite(activeTimestamp) ||
    wouldReplaceNativeBar ||
    (activeTimestamp < latestTimestamp && !replacesImmediatelyPreviousDerivedBar)
  ) {
    return currentPayload;
  }

  const bars = [...currentBars];
  if (matchingIndex >= 0) {
    bars[matchingIndex] = activeBar;
  } else {
    bars.push(activeBar);
  }
  return {
    ...currentPayload,
    source: {
      ...(currentPayload.source || {}),
      last_bar_at: laterTimestampValue(
        currentPayload.source?.last_bar_at,
        activeBar.t
      ),
      last_update_at: laterTimestampValue(
        currentPayload.source?.last_update_at,
        activeBar.t
      ),
    },
    bars,
  };
}

export function reconcileStreamedActiveFiveMinuteBar(
  currentPayload,
  activeBar,
  streamedAt,
  { allowPreviousDerivedReplacement = false } = {}
) {
  const reconciled = reconcileActiveFiveMinuteBar(currentPayload, activeBar, {
    allowPreviousDerivedReplacement,
  });
  if (reconciled === currentPayload) return currentPayload;
  const updatesLiveEdge =
    parsedTimestamp(activeBar.t) >=
    parsedTimestamp(currentPayload.source?.last_bar_at);
  return {
    ...reconciled,
    source: {
      ...(reconciled.source || {}),
      provider: "Alpaca",
      feed: "iex",
      limited_coverage: true,
      is_delayed: false,
      last_bar_at: laterTimestampValue(
        currentPayload.source?.last_bar_at,
        activeBar.t
      ),
      last_update_at: laterTimestampValue(
        currentPayload.source?.last_update_at,
        streamedAt || activeBar.t
      ),
      active_candle_is_partial: updatesLiveEdge
        ? activeBar.is_partial === true
        : currentPayload.source?.active_candle_is_partial === true,
      is_streaming: true,
      refresh_mode: "authenticated_streaming",
    },
  };
}

export function reconcileAuthenticatedOvernightBars(
  currentPayload,
  overnightPayload,
  symbol
) {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  const historicalBars = overnightPayload?.bars?.[normalizedSymbol];
  const latestBar = overnightPayload?.latest?.bars?.[normalizedSymbol];
  if (
    currentPayload?.source?.provider !== "Alpaca" ||
    currentPayload?.source?.feed !== "boats" ||
    overnightPayload?.source?.provider !== "Alpaca" ||
    overnightPayload?.source?.feed !== "boats" ||
    !Array.isArray(historicalBars)
  ) {
    return currentPayload;
  }

  const reconciledHistory = reconcileBackgroundBars(currentPayload, {
    symbol: normalizedSymbol,
    timeframe: "5m",
    bars: historicalBars,
    source: overnightPayload.source,
  });
  if (
    overnightPayload?.latest?.source?.provider !== "Alpaca" ||
    overnightPayload?.latest?.source?.feed !== "overnight" ||
    overnightPayload?.latest?.source?.evidence_timeframe !== "1m" ||
    !latestBar
  ) {
    return reconciledHistory;
  }

  const activeBar = authenticatedActiveFiveMinuteBar(
    {
      symbol: normalizedSymbol,
      timeframe: "1m",
      bars: [latestBar],
      source: overnightPayload.latest.source,
    },
    null,
    { expectedFeed: "overnight", forcePartial: true }
  );
  if (!activeBar) return reconciledHistory;

  const reconciled = reconcileActiveFiveMinuteBar(
    reconciledHistory,
    activeBar,
    { preserveMatchingNativeBar: true }
  );
  if (reconciled === reconciledHistory) return reconciledHistory;

  return {
    ...reconciled,
    source: {
      ...(reconciled.source || {}),
      provider: "Alpaca",
      feed: "boats",
      limited_coverage: true,
      is_delayed: false,
      last_bar_at: laterTimestampValue(
        reconciledHistory.source?.last_bar_at,
        activeBar.t
      ),
      last_update_at: laterTimestampValue(
        reconciledHistory.source?.last_update_at,
        latestBar.t
      ),
      active_candle_is_partial: true,
      active_candle_evidence_feed: "overnight",
      active_candle_evidence_timeframe: "1m",
      is_streaming: false,
      refresh_mode: "authenticated_overnight_polling",
    },
  };
}

function rangeStart(rangeKey, now = new Date()) {
  if (rangeKey === "MAX") return null;
  const end = new Date(now);
  let days = null;
  if (rangeKey === "1D") days = 1;
  if (rangeKey === "2D") days = 2;
  if (rangeKey === "5D") days = 5;
  if (rangeKey === "10D") days = 10;
  if (rangeKey === "1M") days = 31;
  if (rangeKey === "3M") days = 93;
  if (rangeKey === "6M") days = 186;
  if (rangeKey === "1Y") days = 365;
  if (rangeKey === "2Y") days = 730;
  if (rangeKey === "5Y") days = 1825;
  if (rangeKey === "YTD") return new Date(end.getFullYear(), 0, 1).getTime();
  return days === null ? null : end.getTime() - days * 24 * 60 * 60 * 1000;
}

function estimatedRangeDays(rangeKey, now = new Date()) {
  if (rangeKey === "MAX") return 10000;
  const start = rangeStart(rangeKey, now);
  if (start === null) return 10000;
  return Math.max(1, Math.ceil((now.getTime() - start) / (24 * 60 * 60 * 1000)));
}

function requestedBarLimit(timeframe, rangeKey) {
  if (rangeKey === "MAX") return 10000;
  const minutes = TIMEFRAME_MINUTES[timeframe] || 5;
  const days = estimatedRangeDays(rangeKey);
  const baseEstimate = timeframe === "1D"
    ? Math.ceil(days * 1.5)
    : Math.ceil(days * (16 * 60) / minutes * 1.2);
  return Math.min(
    10000,
    Math.max(160, Math.ceil(baseEstimate * HISTORY_BUFFER_MULTIPLIER))
  );
}

function buildEmaSeries(bars, period = EMA_PERIOD) {
  if (!Array.isArray(bars) || bars.length < period || period < 1) {
    return bars.map(() => null);
  }

  const values = bars.map(() => null);
  const seed = bars.slice(0, period).reduce((sum, bar) => sum + bar.close, 0) / period;
  values[period - 1] = seed;

  const multiplier = 2 / (period + 1);
  let previous = seed;
  for (let index = period; index < bars.length; index += 1) {
    const next = (bars[index].close - previous) * multiplier + previous;
    values[index] = next;
    previous = next;
  }

  return values;
}

function sessionDateKey(timestamp) {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    return NEW_YORK_SESSION_DATE_FORMATTER.format(parsed);
  } catch (error) {
    return parsed.toISOString().slice(0, 10);
  }
}

function buildSessionVwapSeries(bars) {
  let activeSession = null;
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;

  return bars.map((bar) => {
    const session = sessionDateKey(bar.timestamp);
    if (session !== activeSession) {
      activeSession = session;
      cumulativePriceVolume = 0;
      cumulativeVolume = 0;
    }

    const barVwap = finiteNumber(bar.vwap);
    const volume = finiteNumber(bar.volume);
    if (!session || barVwap === null || volume === null || volume <= 0) return null;

    cumulativePriceVolume += barVwap * volume;
    cumulativeVolume += volume;
    return cumulativeVolume > 0 ? cumulativePriceVolume / cumulativeVolume : null;
  });
}

function buildGeometry(
  visibleBars,
  extraPriceLevels = [],
  horizontalShiftBars = 0,
  timeDomain = null,
  timeframeMinutes = 5,
) {
  if (!visibleBars.length) {
    return {
      candles: [],
      priceTicks: [],
      maxPrice: null,
      minPrice: null,
      priceToY: null,
    };
  }

  const barHigh = Math.max(...visibleBars.map((bar) => bar.high));
  const barLow = Math.min(...visibleBars.map((bar) => bar.low));
  const validExtraLevels = extraPriceLevels.filter((value) => finiteNumber(value) !== null);
  const rawHigh = Math.max(barHigh, ...(validExtraLevels.length ? validExtraLevels : [barHigh]));
  const rawLow = Math.min(barLow, ...(validExtraLevels.length ? validExtraLevels : [barLow]));
  const rawRange = Math.max(rawHigh - rawLow, Math.max(rawHigh * 0.002, 0.01));
  const padding = rawRange * 0.08;
  const maxPrice = rawHigh + padding;
  const minPrice = Math.max(0, rawLow - padding);
  const range = Math.max(maxPrice - minPrice, 0.0001);
  const maxVolume = Math.max(...visibleBars.map((bar) => bar.volume), 1);
  const slot = CHART_WIDTH / visibleBars.length;
  const domainSpan = timeDomain ? timeDomain.endMs - timeDomain.startMs : 0;
  const intervalMs = Math.max(1, timeframeMinutes) * 60 * 1000;
  const clockSlot = domainSpan > 0 ? (intervalMs / domainSpan) * CHART_WIDTH : null;
  const bodyWidth = timeDomain
    ? Math.max(0.8, Math.min((clockSlot || 1) * 0.72, 9))
    : Math.max(2.4, Math.min(slot * 0.58, 9));
  const priceToY = (price) => ((maxPrice - price) / range) * PRICE_HEIGHT + 8;
  const xShift = timeDomain ? 0 : Math.max(0, horizontalShiftBars) * slot;

  const candles = visibleBars.map((bar, index) => {
    const timestampMs = Date.parse(bar.timestamp);
    const clockX =
      timeDomain && domainSpan > 0 && Number.isFinite(timestampMs)
        ? ((timestampMs + intervalMs / 2 - timeDomain.startMs) / domainSpan) * CHART_WIDTH
        : null;
    const x = clockX === null ? slot * index + slot / 2 - xShift : clockX;
    const openY = priceToY(bar.open);
    const closeY = priceToY(bar.close);
    const highY = priceToY(bar.high);
    const lowY = priceToY(bar.low);
    const rising = bar.close >= bar.open;
    const bodyTop = Math.min(openY, closeY);
    const bodyHeight = Math.max(1.5, Math.abs(closeY - openY));
    const volumeHeight = Math.max(1, (bar.volume / maxVolume) * VOLUME_HEIGHT);

    return {
      ...bar,
      x,
      bodyWidth,
      bodyTop,
      bodyHeight,
      highY,
      lowY,
      rising,
      volumeY: VOLUME_TOP + VOLUME_HEIGHT - volumeHeight,
      volumeHeight,
    };
  });

  const priceTicks = Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    return { value: maxPrice - range * ratio, y: 8 + PRICE_HEIGHT * ratio };
  });

  return { candles, priceTicks, maxPrice, minPrice, priceToY };
}

function buildIndicatorPath(candles, series, priceToY) {
  if (!candles.length || !Array.isArray(series) || typeof priceToY !== "function") return "";
  let path = "";
  let segmentOpen = false;

  candles.forEach((candle, index) => {
    const value = finiteNumber(series[index]);
    if (value === null) {
      segmentOpen = false;
      return;
    }
    const command = segmentOpen ? "L" : "M";
    path += `${command}${candle.x.toFixed(2)},${priceToY(value).toFixed(2)} `;
    segmentOpen = true;
  });

  return path.trim();
}

function axisLabels(candles) {
  const onscreenCandles = candles.filter(
    (candle) => candle.x >= 0 && candle.x <= CHART_WIDTH
  );
  if (!onscreenCandles.length) return [];

  const positions = [0, 0.2, 0.4, 0.6, 0.8, 1];
  const firstTime = Date.parse(onscreenCandles[0].timestamp);
  const lastTime = Date.parse(onscreenCandles[onscreenCandles.length - 1].timestamp);
  const includeDate = lastTime - firstTime > 20 * 60 * 60 * 1000;
  const seen = new Set();

  return positions
    .map((ratio) =>
      onscreenCandles[
        Math.min(
          onscreenCandles.length - 1,
          Math.round((onscreenCandles.length - 1) * ratio)
        )
      ]
    )
    .filter((candle) => {
      if (!candle || seen.has(candle.timestamp)) return false;
      seen.add(candle.timestamp);
      return true;
    })
    .map((candle) => ({
      x: candle.x,
      label: formatAxisTime(candle.timestamp, includeDate),
    }));
}

function ProfessionalChart({
  symbol = "PLUG",
  currentPrice = null,
  entry = null,
  stop = null,
  target1 = null,
  target2 = null,
  market = {},
  trade = {},
  snapshot = null,
  quoteSnapshot = null,
  delayedSipQuoteSnapshot = null,
  bundledBars = null,
  dataStatus = "loading",
  controlledTimeframe = null,
  controlledHistoryRange = null,
  controlledNavigation = null,
  activeIndicatorIds = null,
  defaultShowExtendedHours = false,
  fixed24HourTimelineAtLatest = true,
  backgroundRefreshMs = 0,
  activeCandleRefreshMs = 0,
  allowAuthenticatedStream = true,
  publicMarketData = false,
  onTimeframeChange,
  onHistoryRangeChange,
  onNavigationChange,
  onToggleIndicator,
  drawings = [],
  selectedDrawingToolId = null,
  onCreateDrawing,
  onSelectDrawing,
}) {
  const [localTimeframe, setLocalTimeframe] = useState("5m");
  const [localHistoryRange, setLocalHistoryRange] = useState("5D");
  const [localShowVWAP, setLocalShowVWAP] = useState(true);
  const [localShowEMA, setLocalShowEMA] = useState(true);
  const [showExtendedHours, setShowExtendedHours] = useState(Boolean(defaultShowExtendedHours));
  const [isExpanded, setIsExpanded] = useState(false);
  const [barsState, setBarsState] = useState({
    status: "loading",
    data: null,
    error: "",
    requestKey: null,
  });
  const [chartPlan, setChartPlan] = useState(null);
  const [activeStreamHealth, setActiveStreamHealth] = useState({
    requestKey: null,
    healthy: false,
  });
  const [streamedQuoteState, setStreamedQuoteState] = useState({
    requestKey: null,
    trade: null,
  });
  const [panState, setPanState] = useState({
    viewportEnd: null,
    futureSpaceBars: 0,
  });
  const dragState = useRef({ pointerId: null, lastX: null });
  const consumedBundledBarsRef = useRef(null);
  const activeStreamHealthRef = useRef({ requestKey: null, healthy: false });

  const activeTimeframe = controlledTimeframe || localTimeframe;
  const historyRange = controlledHistoryRange || localHistoryRange;
  const navigationControlled =
    controlledNavigation !== null &&
    typeof controlledNavigation === "object" &&
    !Array.isArray(controlledNavigation);
  const activePanState = navigationControlled
    ? normalizeNavigationState(controlledNavigation)
    : panState;
  const navigationStateRef = useRef(activePanState);
  navigationStateRef.current = activePanState;
  traceChartNavigation("ProfessionalChart.render.navigation", {
    symbol,
    reason: "ProfessionalChart render",
    dataStatus,
    activeTimeframe,
    navigationControlled,
    controlledNavigation,
    normalizedNavigation: activePanState,
    localNavigation: panState,
  });
  const indicatorsControlled = Array.isArray(activeIndicatorIds);
  const showVWAP = indicatorsControlled
    ? activeIndicatorIds.includes("vwap")
    : localShowVWAP;
  const showEMA = indicatorsControlled
    ? activeIndicatorIds.includes("ema20")
    : localShowEMA;

  const handleTimeframeChange = (timeframe) => {
    if (typeof onTimeframeChange === "function") {
      onTimeframeChange(timeframe);
      return;
    }
    if (controlledTimeframe === null) setLocalTimeframe(timeframe);
  };

  const handleHistoryRangeChange = (rangeKey) => {
    if (typeof onHistoryRangeChange === "function") {
      onHistoryRangeChange(rangeKey);
      return;
    }
    if (controlledHistoryRange === null) setLocalHistoryRange(rangeKey);
  };

  const handleToggleIndicator = (indicatorId) => {
    if (typeof onToggleIndicator === "function") {
      onToggleIndicator(indicatorId);
      return;
    }
    if (indicatorsControlled) return;
    if (indicatorId === "vwap") setLocalShowVWAP((value) => !value);
    if (indicatorId === "ema20") setLocalShowEMA((value) => !value);
  };

  const applyPanState = (nextStateOrUpdater) => {
    if (!navigationControlled) {
      setPanState((current) => {
        const normalized = normalizeNavigationState(
          typeof nextStateOrUpdater === "function"
            ? nextStateOrUpdater(current)
            : nextStateOrUpdater
        );
        traceChartNavigation("ProfessionalChart.applyPanState.local", {
          symbol,
          reason: "local navigation mutation",
          previousNavigation: current,
          nextNavigation: normalized,
        }, { stack: true });
        return normalized;
      });
      return;
    }

    const current = navigationStateRef.current;
    const nextState =
      typeof nextStateOrUpdater === "function"
        ? nextStateOrUpdater(current)
        : nextStateOrUpdater;
    const normalized = normalizeNavigationState(nextState);
    traceChartNavigation("ProfessionalChart.applyPanState.controlled", {
      symbol,
      reason: "controlled navigation callback",
      previousNavigation: current,
      requestedNavigation: nextState,
      nextNavigation: normalized,
    }, { stack: true });
    navigationStateRef.current = normalized;
    if (typeof onNavigationChange === "function") {
      onNavigationChange(normalized);
    }
  };

  const handleToggleExtendedHours = () => {
    if (activeTimeframe === "1D") return;
    setShowExtendedHours((value) => !value);
    applyPanState({ viewportEnd: null, futureSpaceBars: 0 });
  };

  const source = snapshot?.source || null;
  const feed = source?.feed || undefined;
  const barLimit = useMemo(
    () => requestedBarLimit(activeTimeframe, historyRange),
    [activeTimeframe, historyRange]
  );
  const currentBarsRequestKey = barsRequestKey(
    symbol,
    activeTimeframe,
    feed,
    barLimit
  );
  const activeStreamHealthy =
    activeStreamHealth.requestKey === currentBarsRequestKey &&
    activeStreamHealth.healthy;
  const streamedQuoteTrade =
    streamedQuoteState.requestKey === currentBarsRequestKey
      ? streamedQuoteState.trade
      : null;
  const suppliedBundledBars = matchingBundledBarsData(
    consumedBundledBarsRef.current === bundledBars ? null : bundledBars,
    symbol,
    activeTimeframe,
    feed,
    barLimit
  );
  const effectiveBarsState =
    barsState.requestKey === currentBarsRequestKey
      ? barsState
      : suppliedBundledBars
      ? {
          status: "ready",
          data: suppliedBundledBars,
          error: "",
          requestKey: currentBarsRequestKey,
        }
      : {
          status: "loading",
          data: null,
          error: "",
          requestKey: currentBarsRequestKey,
        };

  useEffect(() => {
    setChartPlan(null);
    if (!navigationControlled) {
      setPanState({ viewportEnd: null, futureSpaceBars: 0 });
    }
  }, [navigationControlled, symbol]);

  useEffect(() => {
    if (activeTimeframe === "1D") setShowExtendedHours(false);
    if (!navigationControlled) {
      setPanState({ viewportEnd: null, futureSpaceBars: 0 });
    }
  }, [activeTimeframe, historyRange, navigationControlled]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsExpanded(false);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded]);

  useEffect(() => {
    const handlePlan = (event) => {
      const detail = event.detail || {};
      if (
        String(detail.symbol || "").toUpperCase() !==
        String(symbol || "").toUpperCase()
      ) {
        return;
      }
      if (!detail.active) {
        setChartPlan(null);
        return;
      }

      const next = {
        side: detail.side,
        quantity: finiteNumber(detail.quantity),
        entry: finiteNumber(detail.entry),
        stop: finiteNumber(detail.stop),
        target1: finiteNumber(detail.target1),
        target2: finiteNumber(detail.target2),
      };
      if (
        [next.entry, next.stop, next.target1, next.target2].some(
          (value) => value === null
        )
      ) {
        setChartPlan(null);
        return;
      }
      setChartPlan(next);
    };

    window.addEventListener(CHART_PLAN_EVENT, handlePlan);
    return () => window.removeEventListener(CHART_PLAN_EVENT, handlePlan);
  }, [symbol]);

  useEffect(() => {
    if (!symbol || dataStatus !== "ready" || !feed) return undefined;
    let active = true;
    const initialBundledBars = matchingBundledBarsData(
      consumedBundledBarsRef.current === bundledBars ? null : bundledBars,
      symbol,
      activeTimeframe,
      feed,
      barLimit
    );

    if (initialBundledBars) {
      consumedBundledBarsRef.current = bundledBars;
      setBarsState({
        status: "ready",
        data: initialBundledBars,
        error: "",
        requestKey: currentBarsRequestKey,
      });
      return undefined;
    }

    setBarsState({
      status: "loading",
      data: null,
      error: "",
      requestKey: currentBarsRequestKey,
    });

    (publicMarketData ? getStockBars(symbol, activeTimeframe, feed, barLimit, { publicMarketData: true }) : getStockBars(symbol, activeTimeframe, feed, barLimit))
      .then((data) => {
        if (!active) return;
        setBarsState({
          status: "ready",
          data,
          error: "",
          requestKey: currentBarsRequestKey,
        });
      })
      .catch((error) => {
        if (!active) return;
        setBarsState({
          status: "error",
          data: null,
          error: error.message || "Historical chart bars are unavailable.",
          requestKey: currentBarsRequestKey,
        });
      });

    return () => {
      active = false;
    };
  }, [
    activeTimeframe,
    barLimit,
    currentBarsRequestKey,
    dataStatus,
    feed,
    symbol,
    publicMarketData,
    bundledBars,
  ]);

  useEffect(() => {
    const refreshMs = Number(backgroundRefreshMs);
    if (
      !symbol ||
      dataStatus !== "ready" ||
      !feed ||
      effectiveBarsState.status !== "ready" ||
      !Number.isFinite(refreshMs) ||
      refreshMs <= 0
    ) {
      return undefined;
    }

    let active = true;
    let timeoutId = null;

    const scheduleRefresh = () => {
      timeoutId = window.setTimeout(refreshBars, refreshMs);
    };

    const refreshBars = () => {
      (publicMarketData ? getStockBars(symbol, activeTimeframe, feed, barLimit, { cacheTtlMs: 0, publicMarketData: true }) : getStockBars(symbol, activeTimeframe, feed, barLimit, { cacheTtlMs: 0 }))
        .then((data) => {
          if (!active) return;
          setBarsState((current) => {
            if (
              !active ||
              current.status !== "ready" ||
              !current.data ||
              current.requestKey !== currentBarsRequestKey
            ) {
              return current;
            }
            return {
              status: "ready",
              data: reconcileBackgroundBars(current.data, data),
              error: "",
              requestKey: current.requestKey,
            };
          });
        })
        .catch(() => {
          // A quiet refresh never replaces a working chart with an error state.
        })
        .finally(() => {
          if (active) scheduleRefresh();
        });
    };

    scheduleRefresh();
    return () => {
      active = false;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [
    activeTimeframe,
    backgroundRefreshMs,
    barLimit,
    currentBarsRequestKey,
    dataStatus,
    effectiveBarsState.status,
    feed,
    publicMarketData,
    symbol,
  ]);

  useEffect(() => {
    const setStreamHealthy = (healthy) => {
      const next = { requestKey: currentBarsRequestKey, healthy };
      activeStreamHealthRef.current = next;
      setActiveStreamHealth(next);
    };

    setStreamHealthy(false);
    setStreamedQuoteState({ requestKey: currentBarsRequestKey, trade: null });
    if (
      !allowAuthenticatedStream ||
      !symbol ||
      activeTimeframe !== "5m" ||
      feed !== "iex" ||
      dataStatus !== "ready" ||
      effectiveBarsState.status !== "ready"
    ) {
      return undefined;
    }

    let active = true;
    const streamedMinutes = new Map();
    let connection = null;
    let livenessTimeoutId = null;
    let subscribedAt = null;
    let lastActiveCandleEventReceivedAt = null;
    let streamCoverageStartedAtProviderTimestamp = null;

    const clearLivenessWatchdog = () => {
      if (livenessTimeoutId !== null) {
        window.clearTimeout(livenessTimeoutId);
        livenessTimeoutId = null;
      }
    };

    const scheduleLivenessWatchdog = () => {
      clearLivenessWatchdog();
      const freshnessEvidenceAt = lastActiveCandleEventReceivedAt ?? subscribedAt;
      if (!Number.isFinite(freshnessEvidenceAt)) {
        setStreamHealthy(false);
        return;
      }
      const remainingFreshnessMs = Math.max(
        0,
        ACTIVE_STREAM_FRESHNESS_MS - (Date.now() - freshnessEvidenceAt)
      );
      livenessTimeoutId = window.setTimeout(() => {
        if (!active) return;
        livenessTimeoutId = null;
        const latestFreshnessEvidenceAt =
          lastActiveCandleEventReceivedAt ?? subscribedAt;
        if (
          Number.isFinite(latestFreshnessEvidenceAt) &&
          Date.now() - latestFreshnessEvidenceAt < ACTIVE_STREAM_FRESHNESS_MS
        ) {
          scheduleLivenessWatchdog();
          return;
        }
        setStreamHealthy(false);
      }, remainingFreshnessMs);
    };

    const markActiveCandleEventReceived = () => {
      lastActiveCandleEventReceivedAt = Date.now();
      setStreamHealthy(true);
      scheduleLivenessWatchdog();
    };

    const markDisconnected = () => {
      if (!active) return;
      clearLivenessWatchdog();
      setStreamHealthy(false);
    };

    try {
      connection = connectStockMarketDataStream(symbol, {
        onStatus: (status) => {
          if (!active) return;
          if (status?.status === "subscribed") {
            if (subscribedAt === null) subscribedAt = Date.now();
            // A subscription acknowledgement confirms transport only. Keep the
            // authenticated 1m bootstrap active until an actual market event
            // proves that the stream is carrying current symbol data.
            scheduleLivenessWatchdog();
          }
          if (status?.status === "error") markDisconnected();
        },
        onDisconnect: markDisconnected,
        onEvent: (event) => {
          if (
            !active ||
            (event?.type !== "bar" &&
              event?.type !== "updatedBar" &&
              event?.type !== "trade") ||
            event?.symbol !== String(symbol).trim().toUpperCase() ||
            event?.feed !== "iex" ||
            !event?.t
          ) {
            return;
          }

          const providerEventTimestamp = Date.parse(event.t);
          if (
            streamCoverageStartedAtProviderTimestamp === null &&
            Number.isFinite(providerEventTimestamp)
          ) {
            streamCoverageStartedAtProviderTimestamp = providerEventTimestamp;
          }
          if (event.type === "trade") {
            const tradePrice = finiteNumber(event.p);
            if (tradePrice !== null && tradePrice > 0) {
              setStreamedQuoteState({
                requestKey: currentBarsRequestKey,
                trade: { p: tradePrice, t: event.t },
              });
            }
          }
          if (
            (event.type !== "bar" && event.type !== "updatedBar") ||
            event.timeframe !== "1m"
          ) {
            return;
          }

          // Trade events keep the quote current but do not advance OHLC candle
          // state. Only authenticated 1m bar evidence can suppress the REST
          // active-candle fallback.
          markActiveCandleEventReceived();

          const eventTimestamp = providerEventTimestamp;
          if (!Number.isFinite(eventTimestamp)) return;
          const eventBucketTimestamp =
            Math.floor(eventTimestamp / ACTIVE_CANDLE_BUCKET_MS) *
            ACTIVE_CANDLE_BUCKET_MS;

          streamedMinutes.set(event.t, event);
          let sortedMinutes = [...streamedMinutes.values()]
            .filter((bar) => Number.isFinite(Date.parse(bar?.t)))
            .sort(
            (left, right) => Date.parse(left.t) - Date.parse(right.t)
          );
          const newestProviderTimestamp = Date.parse(sortedMinutes.at(-1)?.t);
          const newestProviderBucketTimestamp =
            Math.floor(newestProviderTimestamp / ACTIVE_CANDLE_BUCKET_MS) *
            ACTIVE_CANDLE_BUCKET_MS;
          const previousProviderBucketTimestamp =
            newestProviderBucketTimestamp - ACTIVE_CANDLE_BUCKET_MS;
          sortedMinutes = sortedMinutes.filter((bar) => {
            const timestamp = Date.parse(bar.t);
            const bucketTimestamp =
              Math.floor(timestamp / ACTIVE_CANDLE_BUCKET_MS) *
              ACTIVE_CANDLE_BUCKET_MS;
            const retained =
              bucketTimestamp === newestProviderBucketTimestamp ||
              bucketTimestamp === previousProviderBucketTimestamp;
            if (!retained) streamedMinutes.delete(bar.t);
            return retained;
          });
          while (sortedMinutes.length > ACTIVE_CANDLE_1M_LIMIT) {
            const removed = sortedMinutes.shift();
            streamedMinutes.delete(removed.t);
          }

          const correctsPreviousProviderBucket =
            event.type === "updatedBar" &&
            eventBucketTimestamp === previousProviderBucketTimestamp;
          if (
            eventBucketTimestamp !== newestProviderBucketTimestamp &&
            !correctsPreviousProviderBucket
          ) {
            return;
          }
          const targetBucketTimestamp = correctsPreviousProviderBucket
            ? previousProviderBucketTimestamp
            : newestProviderBucketTimestamp;

          const activeBar = authenticatedActiveFiveMinuteBar(
            {
              symbol: String(symbol).trim().toUpperCase(),
              timeframe: "1m",
              bars: sortedMinutes,
              source: { provider: "Alpaca", feed: "iex" },
            },
            targetBucketTimestamp,
            { streamCoverageStartedAtProviderTimestamp }
          );
          if (!activeBar) return;

          setBarsState((current) => {
            if (
              !active ||
              current.status !== "ready" ||
              !current.data ||
              current.requestKey !== currentBarsRequestKey
            ) {
              return current;
            }
            const dataWithActiveBar = reconcileStreamedActiveFiveMinuteBar(
              current.data,
              activeBar,
              event.t,
              {
                allowPreviousDerivedReplacement: correctsPreviousProviderBucket,
              }
            );
            if (dataWithActiveBar === current.data) return current;
            return {
              status: "ready",
              data: dataWithActiveBar,
              error: "",
              requestKey: current.requestKey,
            };
          });
        },
      });
    } catch (_error) {
      markDisconnected();
    }

    return () => {
      active = false;
      clearLivenessWatchdog();
      activeStreamHealthRef.current = {
        requestKey: currentBarsRequestKey,
        healthy: false,
      };
      if (connection) connection.close();
    };
  }, [
    allowAuthenticatedStream,
    activeTimeframe,
    currentBarsRequestKey,
    dataStatus,
    effectiveBarsState.status,
    feed,
    symbol,
  ]);

  useEffect(() => {
    const refreshMs = Number(activeCandleRefreshMs);
    const nativeRefreshMs = Number(backgroundRefreshMs);
    if (
      !symbol ||
      activeTimeframe !== "5m" ||
      feed !== "iex" ||
      dataStatus !== "ready" ||
      effectiveBarsState.status !== "ready" ||
      activeStreamHealthy ||
      (activeStreamHealthRef.current.requestKey === currentBarsRequestKey &&
        activeStreamHealthRef.current.healthy) ||
      !Number.isFinite(nativeRefreshMs) ||
      nativeRefreshMs <= 0 ||
      !Number.isFinite(refreshMs) ||
      refreshMs <= 0
    ) {
      return undefined;
    }

    let active = true;
    let timeoutId = null;

    const scheduleRefresh = () => {
      timeoutId = window.setTimeout(refreshActiveCandle, refreshMs);
    };

    const refreshActiveCandle = () => {
      (publicMarketData ? getStockBars(symbol, "1m", feed, ACTIVE_CANDLE_1M_LIMIT, { cacheTtlMs: 0, publicMarketData: true }) : getStockBars(symbol, "1m", feed, ACTIVE_CANDLE_1M_LIMIT, { cacheTtlMs: 0 }))
        .then((data) => {
          if (!active) return;
          const activeBar = authenticatedActiveFiveMinuteBar(data);
          if (!activeBar) return;
          setBarsState((current) => {
            if (
              !active ||
              current.status !== "ready" ||
              !current.data ||
              current.requestKey !== currentBarsRequestKey
            ) {
              return current;
            }
            const dataWithActiveBar = reconcileActiveFiveMinuteBar(
              current.data,
              activeBar
            );
            if (dataWithActiveBar === current.data) return current;
            return {
              status: "ready",
              data: dataWithActiveBar,
              error: "",
              requestKey: current.requestKey,
            };
          });
        })
        .catch(() => {
          // The authenticated native chart remains unchanged if active-bar polling fails.
        })
        .finally(() => {
          if (active) scheduleRefresh();
        });
    };

    refreshActiveCandle();
    return () => {
      active = false;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [
    activeCandleRefreshMs,
    activeStreamHealthy,
    activeTimeframe,
    backgroundRefreshMs,
    currentBarsRequestKey,
    dataStatus,
    effectiveBarsState.status,
    feed,
    publicMarketData,
    symbol,
  ]);

  useEffect(() => {
    const refreshMs = Number(activeCandleRefreshMs);
    if (
      publicMarketData ||
      !symbol ||
      activeTimeframe !== "5m" ||
      feed !== "boats" ||
      dataStatus !== "ready" ||
      effectiveBarsState.status !== "ready" ||
      !Number.isFinite(refreshMs) ||
      refreshMs <= 0 ||
      typeof getOvernightStockBars !== "function"
    ) {
      return undefined;
    }

    let active = true;
    let timeoutId = null;

    const scheduleRefresh = () => {
      timeoutId = window.setTimeout(refreshOvernightEvidence, refreshMs);
    };

    const refreshOvernightEvidence = () => {
      getOvernightStockBars([symbol], { cacheTtlMs: 0 })
        .then((data) => {
          if (!active) return;
          setBarsState((current) => {
            if (
              !active ||
              current.status !== "ready" ||
              !current.data ||
              current.requestKey !== currentBarsRequestKey
            ) {
              return current;
            }
            const reconciled = reconcileAuthenticatedOvernightBars(
              current.data,
              data,
              symbol
            );
            if (reconciled === current.data) return current;
            return {
              status: "ready",
              data: reconciled,
              error: "",
              requestKey: current.requestKey,
            };
          });
        })
        .catch(() => {
          // Preserve entitlement-safe BOATS history if latest evidence is unavailable.
        })
        .finally(() => {
          if (active) scheduleRefresh();
        });
    };

    refreshOvernightEvidence();
    return () => {
      active = false;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [
    activeCandleRefreshMs,
    activeTimeframe,
    currentBarsRequestKey,
    dataStatus,
    effectiveBarsState.status,
    feed,
    publicMarketData,
    symbol,
  ]);

  const allBars = useMemo(
    () => normalizeBars(effectiveBarsState.data),
    [effectiveBarsState.data]
  );
  const chartBars = useMemo(
    () =>
      barsForSessionVisibility(allBars, {
        showExtendedHours,
        daily: activeTimeframe === "1D",
      }),
    [activeTimeframe, allBars, showExtendedHours]
  );
  const emaSeries = useMemo(() => buildEmaSeries(chartBars), [chartBars]);
  const vwapSeries = useMemo(
    () =>
      activeTimeframe === "1D"
        ? chartBars.map(() => null)
        : buildSessionVwapSeries(chartBars),
    [activeTimeframe, chartBars]
  );
  const hasEmaSeries = emaSeries.some((value) => finiteNumber(value) !== null);
  const hasVwapSeries = vwapSeries.some((value) => finiteNumber(value) !== null);

  const use24HourTimeline =
    fixed24HourTimelineAtLatest &&
    showExtendedHours &&
    activeTimeframe !== "1D" &&
    activePanState.viewportEnd === null &&
    activePanState.futureSpaceBars === 0 &&
    chartBars.length > 0;
  const timelineDomain = useMemo(
    () =>
      use24HourTimeline
        ? buildNewYork24HourDomain(chartBars[chartBars.length - 1]?.timestamp)
        : null,
    [chartBars, use24HourTimeline]
  );
  const timelineBars = useMemo(
    () => (timelineDomain ? barsInsideTimelineDomain(chartBars, timelineDomain) : []),
    [chartBars, timelineDomain]
  );
  const timelineStartIndex =
    timelineBars.length > 0 ? Math.max(0, chartBars.indexOf(timelineBars[0])) : 0;

  const effectiveEnd = Math.min(
    chartBars.length,
    activePanState.viewportEnd === null ? chartBars.length : activePanState.viewportEnd
  );
  const windowStart = use24HourTimeline
    ? timelineStartIndex
    : Math.max(0, effectiveEnd - MAX_VISIBLE_BARS);
  const visibleBars = use24HourTimeline
    ? timelineBars
    : chartBars.slice(windowStart, effectiveEnd);
  const visibleSeriesEnd = use24HourTimeline
    ? windowStart + visibleBars.length
    : effectiveEnd;
  const visibleEma = emaSeries.slice(windowStart, visibleSeriesEnd);
  const visibleVwap = vwapSeries.slice(windowStart, visibleSeriesEnd);
  const maxFutureSpaceForViewport = Math.min(
    MAX_FUTURE_SPACE_BARS,
    Math.max(0, Math.floor(visibleBars.length * 0.62))
  );
  const futureSpaceBars = use24HourTimeline
    ? 0
    : activePanState.viewportEnd === null
    ? 0
    : Math.min(activePanState.futureSpaceBars, maxFutureSpaceForViewport);
  const canGoOlder = windowStart > 0;
  const canGoNewer = effectiveEnd < chartBars.length;
  const isAtLatest =
    activePanState.viewportEnd === null &&
    !canGoNewer &&
    futureSpaceBars === 0;

  traceChartNavigation("ProfessionalChart.render.effectiveNavigation", {
    symbol,
    reason: "visible bars and effective future-space calculation",
    activeTimeframe,
    showExtendedHours,
    fixed24HourTimelineAtLatest,
    use24HourTimeline,
    chartBars: chartBars.length,
    visibleBars: visibleBars.length,
    activeNavigation: activePanState,
    effectiveEnd,
    windowStart,
    maxFutureSpaceForViewport,
    effectiveFutureSpaceBars: futureSpaceBars,
    horizontalShiftBars: futureSpaceBars,
    canGoOlder,
    canGoNewer,
    isAtLatest,
  });

  const planLevels = useMemo(
    () =>
      chartPlan
        ? [chartPlan.entry, chartPlan.stop, chartPlan.target1, chartPlan.target2]
        : [],
    [chartPlan]
  );
  const indicatorLevels = useMemo(
    () =>
      [...visibleEma, ...visibleVwap].filter(
        (value) => finiteNumber(value) !== null
      ),
    [visibleEma, visibleVwap]
  );
  const geometry = useMemo(
    () =>
      buildGeometry(
        visibleBars,
        [...planLevels, ...indicatorLevels],
        futureSpaceBars + (!use24HourTimeline && isAtLatest ? 4 : 0),
        use24HourTimeline ? timelineDomain : null,
        TIMEFRAME_MINUTES[activeTimeframe] || 5,
      ),
    [
      activeTimeframe,
      visibleBars,
      indicatorLevels,
      planLevels,
      futureSpaceBars,
      isAtLatest,
      timelineDomain,
      use24HourTimeline,
    ]
  );
  traceChartNavigation("ProfessionalChart.render.geometry", {
    symbol,
    reason: "geometry completed",
    activeTimeframe,
    effectiveFutureSpaceBars: futureSpaceBars,
    horizontalShiftBars: futureSpaceBars,
    candleCount: geometry.candles.length,
    firstCandleX: geometry.candles[0]?.x ?? null,
    lastCandleX: geometry.candles.at(-1)?.x ?? null,
    timelineDomain,
  });
  const emaPath = useMemo(
    () => buildIndicatorPath(geometry.candles, visibleEma, geometry.priceToY),
    [geometry.candles, geometry.priceToY, visibleEma]
  );
  const vwapPath = useMemo(
    () => buildIndicatorPath(geometry.candles, visibleVwap, geometry.priceToY),
    [geometry.candles, geometry.priceToY, visibleVwap]
  );
  const timeAxis = useMemo(
    () =>
      use24HourTimeline && timelineDomain
        ? buildNewYork24HourAxis(timelineDomain, CHART_WIDTH)
        : axisLabels(geometry.candles),
    [geometry.candles, timelineDomain, use24HourTimeline]
  );
  const extendedSessionBands = useMemo(() => {
    if (!showExtendedHours || activeTimeframe === "1D") return [];
    if (use24HourTimeline && timelineDomain) {
      return buildScheduledExtendedSessionBands(timelineDomain, CHART_WIDTH);
    }
    return buildExtendedSessionBands(geometry.candles, CHART_WIDTH);
  }, [
    activeTimeframe,
    geometry.candles,
    showExtendedHours,
    timelineDomain,
    use24HourTimeline,
  ]);

  const shiftViewport = (direction, bars = VIEWPORT_STEP, allowFutureSpace = false) => {
    if (!chartBars.length) return;
    const amount = Math.max(1, bars);

    applyPanState((current) => {
      const total = chartBars.length;
      let currentEnd =
        current.viewportEnd === null
          ? total
          : Math.min(total, current.viewportEnd);
      let currentFutureSpace = Math.min(
        current.futureSpaceBars,
        MAX_FUTURE_SPACE_BARS
      );
      let remaining = amount;

      if (direction === "older") {
        if (currentFutureSpace > 0) {
          const consumed = Math.min(currentFutureSpace, remaining);
          currentFutureSpace -= consumed;
          remaining -= consumed;
        }

        if (remaining > 0) {
          const minimumEnd = Math.min(MAX_VISIBLE_BARS, total);
          currentEnd = Math.max(minimumEnd, currentEnd - remaining);
        }
      }

      if (direction === "newer") {
        if (currentEnd < total) {
          const consumed = Math.min(remaining, total - currentEnd);
          currentEnd += consumed;
          remaining -= consumed;
        }

        if (allowFutureSpace && currentEnd >= total && remaining > 0) {
          const visibleCount = Math.min(MAX_VISIBLE_BARS, total);
          const maxFuture = Math.min(
            MAX_FUTURE_SPACE_BARS,
            Math.max(0, Math.floor(visibleCount * 0.62))
          );
          currentFutureSpace = Math.min(
            maxFuture,
            currentFutureSpace + remaining
          );
        }
      }

      return {
        viewportEnd:
          currentFutureSpace > 0
            ? total
            : currentEnd >= total
            ? null
            : currentEnd,
        futureSpaceBars: currentFutureSpace,
      };
    });
  };

  const handlePointerDown = (event) => {
    dragState.current = {
      pointerId: event.pointerId,
      lastX: event.clientX,
    };
    if (typeof event.currentTarget.setPointerCapture === "function") {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is an interaction enhancement; navigation still works without it.
      }
    }
  };

  const handlePointerMove = (event) => {
    const drag = dragState.current;
    if (drag.lastX === null || drag.pointerId !== event.pointerId) return;
    const delta = event.clientX - drag.lastX;
    if (Math.abs(delta) < DRAG_STEP_PX) return;

    const barsToPan = Math.max(1, Math.floor(Math.abs(delta) / DRAG_STEP_PX));
    const movingTowardOlder = delta > 0;
    shiftViewport(
      movingTowardOlder ? "older" : "newer",
      barsToPan,
      !movingTowardOlder
    );
    dragState.current.lastX = event.clientX;
    if (event.cancelable) event.preventDefault();
  };

  const endPointerDrag = (event) => {
    if (
      dragState.current.pointerId !== null &&
      typeof event.currentTarget.releasePointerCapture === "function"
    ) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // The pointer may already have been released by the browser.
      }
    }
    dragState.current = { pointerId: null, lastX: null };
  };

  const dailyBar = snapshot?.daily_bar || {};
  const previousDailyBar = snapshot?.previous_daily_bar || {};
  const quoteBadge = sessionQuoteBadge(
    quoteSnapshot || snapshot,
    delayedSipQuoteSnapshot,
    streamedQuoteTrade
  );
  const current = finiteNumber(currentPrice);
  const previousClose = finiteNumber(previousDailyBar.c);
  const dayChange =
    current !== null && previousClose !== null ? current - previousClose : null;
  const dayChangePercent =
    dayChange !== null && previousClose > 0
      ? (dayChange / previousClose) * 100
      : null;
  const riskIsHigh =
    market?.riskLevel === "High" || market?.riskLevel === "Very High";
  const direction = trade?.direction || "MONITORING";
  const hasLiveSnapshot = Boolean(snapshot && current !== null);
  const isLoading = dataStatus === "loading";
  const isHistoryLoading =
    dataStatus === "ready" &&
    hasLiveSnapshot &&
    effectiveBarsState.status === "loading";
  const isChartLoading = isLoading || isHistoryLoading;
  const isUnavailable = dataStatus === "error" || (!isLoading && !hasLiveSnapshot);
  const barsReady = effectiveBarsState.status === "ready" && chartBars.length > 0;
  const barsSource = effectiveBarsState.data?.source || null;
  const latestBar = chartBars.length ? chartBars[chartBars.length - 1] : null;
  const latestPayloadBar = Array.isArray(effectiveBarsState.data?.bars)
    ? effectiveBarsState.data.bars.reduce(
        (latest, bar) =>
          parsedTimestamp(bar?.t) > parsedTimestamp(latest?.t) ? bar : latest,
        null
      )
    : null;
  const derivedActiveFiveMinuteBar =
    activeTimeframe === "5m" &&
    (feed === "iex" || feed === "boats") &&
    effectiveBarsState.data?.symbol === String(symbol || "").toUpperCase() &&
    isDerivedActiveFiveMinuteBar(latestPayloadBar)
      ? latestPayloadBar
      : null;
  const hasDerivedActiveFiveMinuteBar = Boolean(derivedActiveFiveMinuteBar);
  const activeCandleEvidenceFeed =
    derivedActiveFiveMinuteBar?.[OVERNIGHT_ACTIVE_CANDLE_DERIVED_FLAG] === true
      ? "overnight latest 1m evidence"
      : "IEX 1m bars";
  const planLineItems =
    chartPlan && geometry.priceToY
      ? [
          { key: "entry", label: "Entry", value: chartPlan.entry, className: "entry" },
          { key: "stop", label: "Stop", value: chartPlan.stop, className: "stop" },
          { key: "target1", label: "Target 1", value: chartPlan.target1, className: "target" },
          { key: "target2", label: "Target 2", value: chartPlan.target2, className: "target" },
        ]
      : [];

  const chartContent = (
    <section
      className={`professionalChartShell${isExpanded ? " professionalChartExpanded" : ""}`}
      data-testid="professional-live-chart"
      aria-label={isExpanded ? `${symbol} expanded live chart` : undefined}
    >
      <div className="professionalChartHeader">
        <div className="professionalChartQuote">
          <div className="professionalChartSymbolRow">
            <strong>{symbol}</strong>
            <span className="professionalChartMarketBadge">
              {isLoading
                ? "Loading authenticated market data"
                : hasLiveSnapshot
                  ? source?.label || "Alpaca Market Data"
                  : "Market data unavailable"}
            </span>
          </div>
          <div className="professionalChartPriceRow">
            <span data-testid="professional-chart-current-price">{formatPrice(current)}</span>
            <small className={dayChange !== null && dayChange < 0 ? "negative" : ""}>
              {dayChange === null || dayChangePercent === null
                ? "Session change unavailable"
                : `${dayChange >= 0 ? "+" : ""}${formatPrice(dayChange)} (${dayChangePercent >= 0 ? "+" : ""}${dayChangePercent.toFixed(2)}%)`}
            </small>
          </div>
        </div>
        <div className="professionalChartStatus">
          <span>{isLoading ? "LOADING" : isUnavailable ? "DATA BLOCKED" : trade?.stage || "MONITORING"}</span>
          {!String(trade?.stage || "").toUpperCase().includes("BLOCKED") && (
            <strong className={direction === "LONG" ? "positive" : direction === "DEFENSIVE" ? "danger" : "warning"}>
              {isLoading || isUnavailable ? "WAIT" : direction}
            </strong>
          )}
        </div>
      </div>

      <div className="professionalChartToolbar">
        <div className="professionalTimeframes" aria-label="Candle interval">
          {TIMEFRAMES.map((timeframe) => (
            <button
              type="button"
              key={timeframe}
              className={activeTimeframe === timeframe ? "active" : ""}
              onClick={() => handleTimeframeChange(timeframe)}
              title={`Load authenticated Alpaca ${timeframe} historical bars`}
            >
              {timeframe}
            </button>
          ))}
        </div>
        <div className="professionalChartTools">
          <button
            type="button"
            className={`extendedHoursControl${showExtendedHours && activeTimeframe !== "1D" ? " activeTool" : ""}`}
            aria-label="Extended hours"
            aria-pressed={activeTimeframe !== "1D" && showExtendedHours}
            onClick={handleToggleExtendedHours}
            disabled={activeTimeframe === "1D"}
            title={
              activeTimeframe === "1D"
                ? "Extended-hours session display is available on intraday candles"
                : showExtendedHours
                ? "Hide authenticated premarket and after-hours candles"
                : "Show authenticated premarket and after-hours candles"
            }
          >EXT</button>
          <button type="button" disabled>+ Indicators</button>
          <button
            type="button"
            className={showVWAP && hasVwapSeries ? "activeTool" : ""}
            onClick={() => handleToggleIndicator("vwap")}
            disabled={!hasVwapSeries}
            title={hasVwapSeries ? "Toggle authenticated session VWAP" : "Authenticated session VWAP unavailable for this bar set"}
          >VWAP</button>
          <button
            type="button"
            className={showEMA && hasEmaSeries ? "activeTool" : ""}
            onClick={() => handleToggleIndicator("ema20")}
            disabled={!hasEmaSeries}
            title={hasEmaSeries ? "Toggle EMA 20 derived from authenticated closes" : "At least 20 authenticated closes are required for EMA 20"}
          >EMA</button>
          <button type="button" aria-label="Chart settings" disabled>⚙</button>
          <button
            type="button"
            aria-label={isExpanded ? "Exit expanded chart" : "Expand chart"}
            className={isExpanded ? "activeTool" : ""}
            onClick={() => setIsExpanded((value) => !value)}
            title={isExpanded ? "Exit expanded chart (Esc)" : "Expand chart"}
          >
            {isExpanded ? "✕" : "⛶"}
          </button>
        </div>
      </div>

      <div className="professionalRangeBar" aria-label="Chart history range">
        <span>Range</span>
        <div className="professionalRangeButtons">
          {HISTORY_RANGES.map((rangeKey) => (
            <button
              type="button"
              key={rangeKey}
              className={historyRange === rangeKey ? "active" : ""}
              onClick={() => handleHistoryRangeChange(rangeKey)}
            >
              {rangeKey}
            </button>
          ))}
        </div>
        <div className="professionalHistoryNavigation" aria-label="Chart history navigation">
          <button type="button" onClick={() => shiftViewport("older")} disabled={!canGoOlder && futureSpaceBars === 0}>← Older</button>
          <button type="button" onClick={() => shiftViewport("newer")} disabled={!canGoNewer}>Newer →</button>
          <button
            type="button"
            className={isAtLatest ? "active" : ""}
            onClick={() => applyPanState({ viewportEnd: null, futureSpaceBars: 0 })}
            disabled={isAtLatest}
          >Latest</button>
        </div>
      </div>

      <div className="professionalChartMeta" aria-label="Authenticated Alpaca snapshot values">
        <span>O {formatPrice(dailyBar.o)}</span>
        <span>H {formatPrice(dailyBar.h)}</span>
        <span>L {formatPrice(dailyBar.l)}</span>
        <span>C {formatPrice(current)}</span>
        <span>Vol {formatVolume(dailyBar.v)}</span>
        <span>Range {historyRange}</span>
        <span>Interval {activeTimeframe}</span>
        <span>Hours {activeTimeframe === "1D" ? "Daily" : showExtendedHours ? "Extended" : "Regular"}</span>
      </div>

      <div
        className="chartBox proChart professionalPanChart"
        data-tour-id="professional-chart"
        data-testid="professional-pan-chart"
        data-future-space-bars={futureSpaceBars}
        data-clock-domain={use24HourTimeline ? "24h" : "bars"}
        aria-label="Interactive authenticated chart. Drag right for older history or left toward newer history and future chart space."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointerDrag}
        onPointerCancel={endPointerDrag}
      >
        {isChartLoading ? (
          <div className="scannerChartUnavailable" role="status">
            <strong>Loading authenticated market data</strong>
            <p>Fetching a fresh authenticated Alpaca quote and chart context.</p>
          </div>
        ) : isUnavailable ? (
          <div className="scannerChartUnavailable" role="status">
            <strong>Authenticated live chart data unavailable</strong>
            <p>No synthetic price, candle, OHLC, volume, or timestamp has been substituted.</p>
          </div>
        ) : effectiveBarsState.status === "error" ? (
          <div className="scannerChartUnavailable" role="alert">
            <strong>Historical chart bars unavailable</strong>
            <p>{effectiveBarsState.error} No synthetic candles have been substituted.</p>
          </div>
        ) : !barsReady ? (
          <div className="scannerChartUnavailable" role="status">
            <strong>
              {allBars.length
                ? showExtendedHours
                  ? "No authenticated market-session bars available in this view"
                  : "No authenticated regular-session bars available in this view"
                : `No authenticated bars returned for ${historyRange}`}
            </strong>
            <p>
              {allBars.length && !showExtendedHours
                ? "Use EXT to reveal authenticated premarket and after-hours bars when the provider returned them. No synthetic candles were substituted."
                : "Alpaca returned no visible bars for this request. No synthetic candles were substituted."}
            </p>
          </div>
        ) : (
          <>
            <svg
              className="professionalHistoryChart"
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`${symbol} authenticated Alpaca ${activeTimeframe} candlestick chart, ${historyRange} history range${use24HourTimeline ? ", 24-hour New York timeline" : ""}`}
            >
              {extendedSessionBands.map((band) => (
                <g key={band.id} data-testid={`extended-session-${band.session}`}>
                  <rect
                    className={`historyExtendedSessionBand ${band.session}`}
                    x={band.x}
                    y="0"
                    width={band.width}
                    height={VOLUME_TOP + VOLUME_HEIGHT}
                  />
                  <line
                    className="historyExtendedSessionBoundary"
                    x1={band.x}
                    x2={band.x}
                    y1="0"
                    y2={VOLUME_TOP + VOLUME_HEIGHT}
                  />
                  <text
  className="historyExtendedSessionLabel"
  x={
    band.session === "after-hours"
      ? Math.max(8, band.x - 6)
      : Math.min(CHART_WIDTH - 8, band.x + 6)
  }
  y="18"
  textAnchor={band.session === "after-hours" ? "end" : "start"}
>
                    {[band.label, band.startLabel, band.endLabel].filter(Boolean).join(" · ")}
                  </text>
                </g>
              ))}
              {geometry.priceTicks.map((tick) => (
                <g key={`grid-${tick.y}`}>
                  <line className="historyGridLine" x1="0" x2={CHART_WIDTH} y1={tick.y} y2={tick.y} />
                  <text className="historyPriceLabel" x={CHART_WIDTH - 8} y={tick.y - 4} textAnchor="end">
                    {formatPrice(tick.value)}
                  </text>
                </g>
              ))}
              <line className="historyVolumeDivider" x1="0" x2={CHART_WIDTH} y1={VOLUME_TOP - 8} y2={VOLUME_TOP - 8} />
              {geometry.candles.map((candle, index) => (
                <g key={`${candle.timestamp}-${index}`}>
                  <line
                    className={candle.rising ? "historyWick rising" : "historyWick falling"}
                    x1={candle.x}
                    x2={candle.x}
                    y1={candle.highY}
                    y2={candle.lowY}
                  />
                  <rect
                    className={candle.rising ? "historyCandle rising" : "historyCandle falling"}
                    x={candle.x - candle.bodyWidth / 2}
                    y={candle.bodyTop}
                    width={candle.bodyWidth}
                    height={candle.bodyHeight}
                    rx="1"
                  >
                    <title>{`${formatTime(candle.timestamp)} · O ${formatPrice(candle.open)} H ${formatPrice(candle.high)} L ${formatPrice(candle.low)} C ${formatPrice(candle.close)} · Vol ${formatVolume(candle.volume)}`}</title>
                  </rect>
                  <rect
                    className={candle.rising ? "historyVolume rising" : "historyVolume falling"}
                    x={candle.x - candle.bodyWidth / 2}
                    y={candle.volumeY}
                    width={candle.bodyWidth}
                    height={candle.volumeHeight}
                    rx="1"
                  />
                </g>
              ))}
              {showVWAP && hasVwapSeries && vwapPath && (
                <path data-testid="history-indicator-vwap" className="historyIndicatorLine vwap" d={vwapPath} />
              )}
              {showEMA && hasEmaSeries && emaPath && (
                <path data-testid="history-indicator-ema20" className="historyIndicatorLine ema20" d={emaPath} />
              )}
              <ChartDrawingInteractionLayer
                drawings={drawings}
                selectedToolId={selectedDrawingToolId}
                geometry={geometry}
                chartWidth={CHART_WIDTH}
                priceHeight={PRICE_HEIGHT}
                onCreateDrawing={onCreateDrawing}
                onSelectDrawing={onSelectDrawing}
              />
              {planLineItems.map((item) => {
                const y = geometry.priceToY(item.value);
                return (
                  <g key={item.key} data-testid={`chart-plan-${item.key}`}>
                    <line className={`historyTradePlanLine ${item.className}`} x1="0" x2={CHART_WIDTH} y1={y} y2={y} />
                    <text className={`historyTradePlanLabel ${item.className}`} x="8" y={Math.max(14, y - 5)}>
                      {item.label} {formatPrice(item.value)}
                    </text>
                  </g>
                );
              })}
              {timeAxis.map((item) => (
                <g key={item.id || `time-${item.x}`} data-testid={use24HourTimeline ? "24h-axis-marker" : undefined}>
                  <line className="historyTimeTick" x1={item.x} x2={item.x} y1={VOLUME_TOP + VOLUME_HEIGHT + 2} y2={VOLUME_TOP + VOLUME_HEIGHT + 8} />
                  <text className="historyTimeLabel" x={item.x} y={CHART_HEIGHT - 8} textAnchor="middle">{item.label}</text>
                </g>
              ))}
            </svg>
            <div
              className="professionalLiveQuoteBadge"
              data-testid="professional-live-quote-badge"
            >
              <span>
                {quoteBadge.evidence
                  ? quoteBadge.label
                  : `${quoteBadge.label} — Awaiting live quote`}
              </span>
              {quoteBadge.evidence && (
                <>
                  <strong>{formatPrice(quoteBadge.evidence.price)}</strong>
                  <small>{formatNewYorkTime(quoteBadge.evidence.timestamp)}</small>
                </>
              )}
            </div>
            {riskIsHigh && <div className="entryMarker">🔴 AI: TRADE BLOCKED</div>}
          </>
        )}
      </div>

      {barsReady && (
        <div
          className="professionalHistoryStatusStrip"
          data-testid="professional-history-status"
          aria-label="Authenticated chart history status"
        >
          <span><strong>{allBars.length}</strong> authenticated bars loaded</span>
          <span><strong>{chartBars.length}</strong> {activeTimeframe === "1D" ? "daily" : showExtendedHours ? "market-session" : "regular-session"} bars available</span>
          <span>
            {use24HourTimeline
              ? `24-hour timeline · ${visibleBars.length} authenticated bars in market day`
              : `Viewing ${visibleBars.length} · ${windowStart + 1}-${effectiveEnd}`}
          </span>
          <span>
            {futureSpaceBars > 0
              ? `Future space ${futureSpaceBars} bars`
              : isAtLatest
                ? "At latest"
                : "Historical view"}
          </span>
          <span>Latest candle {latestBar ? formatTime(latestBar.timestamp) : "—"}</span>
          {chartPlan && (
            <span className="professionalTradePlanBadge" role="status">
              <strong>{chartPlan.side === "buy" ? "BUY" : "SELL"} PLAN</strong>
              <span>Chart levels set explicitly · not an executed order</span>
            </span>
          )}
        </div>
      )}

      <div className="professionalChartFooter">
        <div><span>Range</span><strong>{historyRange}</strong></div>
        <div><span>Interval</span><strong>{activeTimeframe}</strong></div>
        <div><span>Hours</span><strong>{activeTimeframe === "1D" ? "Daily" : showExtendedHours ? "Extended" : "Regular"}</strong></div>
        <div><span>Provider</span><strong>{barsSource?.provider || source?.provider || "—"}</strong></div>
        <div><span>Feed</span><strong>{(barsSource?.feed || source?.feed || "").toUpperCase() || "—"}</strong></div>
        <div><span>Latest candle</span><strong>{barsSource?.last_bar_at ? formatTime(barsSource.last_bar_at) : "—"}</strong></div>
        {hasDerivedActiveFiveMinuteBar && (
          <div data-testid="professional-active-candle-status" role="status">
            <small>
              {derivedActiveFiveMinuteBar.is_partial === true
                ? "Partial live candle"
                : "Active 5m candle"}
              {` · aggregated from authenticated Alpaca ${activeCandleEvidenceFeed}`}
            </small>
          </div>
        )}
      </div>

      <div className="professionalChartMeta" role="note">
        <span>
          Range controls the authenticated history request and keeps an older navigation buffer behind the visible chart. Interval controls candle size. EXT reveals only authenticated premarket and after-hours bars returned by the provider; when EXT is off, intraday candles are limited to the regular 9:30 AM–4:00 PM New York session. {fixed24HourTimelineAtLatest
            ? "At Latest with EXT enabled, the chart uses a continuous 4:00 AM–4:00 AM New York clock so authenticated candles remain at their true times and missing evening or overnight coverage remains visibly blank instead of being fabricated."
            : "In this workspace, EXT keeps authenticated premarket and after-hours candles in the same bar-based navigation mode so dragging stays continuous without creating future candles or prices."} Drag right or use Older to move backward through loaded genuine bars. Historical views retain bar-based navigation. Latest resets both history position and future space. Historical candles, volume, session VWAP, and EMA 20 are derived only from authenticated Alpaca bars; no synthetic candle or indicator value is created.
        </span>
        {barsSource?.provider_has_next_page && (
          <span className="professionalHistoryLimitNotice">
            Alpaca reports additional older provider history beyond this response. The chart is showing the newest authenticated page available to this request and does not fabricate the missing older page.
          </span>
        )}
      </div>
    </section>
  );

  if (isExpanded && typeof document !== "undefined") {
    return createPortal(chartContent, document.body);
  }

  return chartContent;
}

export default ProfessionalChart;
